import React from "react";
import { AtSignIcon, InfoIcon, SearchIcon } from "lucide-react";
import { Alert, Badge, Button, Combobox, Input, Spinner, Tooltip } from "@/components/ui";
import { SocialSvg } from "@/components";
import { useSocialProfiles } from "@/utils";
import { useSearchContext } from "../SearchContext";
import { socialNames } from "@/interfaces";

export function SearchControls() {
  const {
    players,
    effectiveUserId,
    networksFilter,
    isPosted,
    isSearching,
    selectedNetwork,
    setSelectedNetwork,
    selectedPlayer,
    setSelectedPlayer,
    handleSearch,
  } = useSearchContext();

  const [searchQuery, setSearchQuery] = React.useState("");
  const showPlayerDropdown = players.length > 1;

  const { socialProfiles, isLoading: isLoadingProfiles } = useSocialProfiles(effectiveUserId || "");

  const activeProfiles = React.useMemo(() => socialProfiles.filter((p: any) => p.isActive), [socialProfiles]);

  const displayedProfiles = React.useMemo(
    () =>
      networksFilter ? activeProfiles.filter((p: any) => networksFilter.includes(p.socialNetworkId)) : activeProfiles,
    [networksFilter, activeProfiles]
  );

  const disabledNetworks = isPosted ? ["TT"] : [];

  // Sync selectedNetwork when player or profiles change
  React.useEffect(() => {
    if (!isLoadingProfiles && displayedProfiles.length > 0) {
      if (!selectedNetwork || !displayedProfiles.some((p: any) => p.socialNetworkId === selectedNetwork)) {
        setSelectedNetwork(displayedProfiles[0].socialNetworkId);
      }
    } else if (!isLoadingProfiles && displayedProfiles.length === 0) {
      setSelectedNetwork(undefined);
    }
  }, [displayedProfiles, isLoadingProfiles, selectedNetwork, setSelectedNetwork]);

  const onSearchClick = () => {
    handleSearch(searchQuery);
  };

  const showCaptionAlert = isPosted && selectedNetwork && !["FB", "LI"].includes(selectedNetwork);

  return (
    <div className="flex flex-col gap-6">
      {showPlayerDropdown && (
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-slate-700">1. Select a Player for searching</label>
          <Combobox
            value={selectedPlayer}
            onValueChange={setSelectedPlayer}
            items={players}
            itemToStringLabel={i => i.name || "Unknown Player"}
            placeholder="Choose a player..."
          />
        </div>
      )}

      <div className="flex flex-col gap-2">
        <label className="text-sm font-semibold text-slate-700">
          {showPlayerDropdown ? "2." : "1."} Select Social Network
        </label>
        {isLoadingProfiles ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
            <Spinner className="size-3" /> Loading connected profiles...
          </div>
        ) : displayedProfiles.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {displayedProfiles.map((profile: any) => {
              const isDisabled = disabledNetworks.includes(profile.socialNetworkId);
              const isSelected = selectedNetwork === profile.socialNetworkId;

              const badge = (
                <Badge
                  key={profile.id}
                  variant={isSelected ? "default" : "outline"}
                  className={`px-3 py-1.5 ${isDisabled ? "opacity-50 grayscale cursor-not-allowed" : "cursor-pointer"}`}
                  onClick={() => !isDisabled && setSelectedNetwork(profile.socialNetworkId)}
                >
                  <SocialSvg
                    networkId={profile.socialNetworkId}
                    bgColorClass={isSelected ? "bg-primary" : "bg-slate-100"}
                    iconColorClass={isSelected ? "text-primary-foreground" : "text-slate-500"}
                    size="xs"
                  />
                </Badge>
              );

              if (isDisabled) {
                return (
                  <Tooltip key={profile.id}>
                    <Tooltip.Trigger render={badge} />
                    <Tooltip.Content>This network doesn't support updating mentions after it's posted</Tooltip.Content>
                  </Tooltip>
                );
              }

              return badge;
            })}
          </div>
        ) : (
          <p className="text-xs text-destructive">No connected social profiles found.</p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm font-semibold text-slate-700">
          {showPlayerDropdown ? "3." : "2."} Search Profile or Page
        </label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input
              placeholder="Enter exact username or page name..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === "Enter" && onSearchClick()}
              className="pl-9"
            />
            <AtSignIcon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-400" />
          </div>
          <Button onClick={onSearchClick} disabled={isSearching || !selectedNetwork || !searchQuery.trim()}>
            {isSearching ? <Spinner className="size-4" /> : <SearchIcon className="size-4" />}
          </Button>
        </div>
      </div>

      {showCaptionAlert && (
        <Alert className="bg-blue-50/50 border-blue-100">
          <InfoIcon className="size-4 text-blue-600" />
          <Alert.Description className="text-blue-800 text-xs leading-relaxed">
            Caption editing is restricted by {socialNames[selectedNetwork]}. New mentions will be automatically added as
            a comment on your post.
          </Alert.Description>
        </Alert>
      )}
    </div>
  );
}
